/**
 * "Remind me later" for home-screen attention items (tasks and class-post
 * acknowledgements). Extracted from the old FamilyHome.tsx (retired when the
 * Family Command Center became the family home - see FamilyCommandCenter.tsx)
 * so the same behavior keeps working from its one remaining call site.
 *
 * This is a LOCAL-DEVICE-ONLY feature, not a Firestore field: the task or
 * post itself stays exactly as open/unacknowledged as it was on the server,
 * we just remember on THIS browser that the family already saw it and asked
 * to be reminded later, so it drops out of the attention stack until that
 * time passes. Switching devices or clearing site data resets it. If a
 * cross-device version is ever wanted, it would need a real field on the
 * task/post plus a rules change; keeping it local keeps this small and safe.
 *
 * Deliberately does NOT cover bulletins (studio announcements) - Bobbie's
 * 2026-08-18 "every message carries Remind me later" ruling (DECISIONS.md)
 * keeps announcements outside the universal message toolkit on purpose, so
 * this module still matches that one named exception.
 *
 * 'notif' (feed-unification Slice B, 2026-08-18,
 * docs/feed-unification-plan-2026-08-18.md) is the GENERIC kind: any
 * in-place message card built from a raw notifications/{id} doc, keyed by
 * the notification's own id. Unlike 'task'/'post' it covers every kind that
 * reaches the merged card stack (class post, board, comp board, bag group,
 * calendar, general, attendance, newsletter, sub, message, mention) with
 * one shared key shape, since none of those kinds had a snooze concept to
 * name individually before this ruling.
 */

export type SnoozeKind = 'task' | 'post' | 'notif';

const SNOOZE_KEY_PREFIX = 'ldg.snooze.';

function snoozeStorageKey(kind: SnoozeKind, uid: string, itemId: string): string {
  return `${SNOOZE_KEY_PREFIX}${kind}.${uid}.${itemId}`;
}

/**
 * The OLD FamilyHome task-snooze key shape, before this module's `.task.`
 * kind infix existed: `ldg.snooze.<uid>.<taskId>`, no infix at all. (Post
 * keys never had this problem - FamilyHome already wrote those under
 * `ldg.snooze.post.<uid>.<postId>`, matching this module's shape exactly.)
 * Without a fallback, a parent who snoozed a task "in 3 days" the day
 * before this page shipped would have that reminder silently reappear the
 * next time they opened the app, since the read would look under a key
 * that was never written. Never collides with a real key: a Firebase uid
 * is never literally "task" or "post".
 */
function legacyTaskSnoozeKey(uid: string, taskId: string): string {
  return `${SNOOZE_KEY_PREFIX}${uid}.${taskId}`;
}

function getSnoozeUntilMs(kind: SnoozeKind, uid: string | undefined, itemId: string): number | null {
  if (!uid || typeof window === 'undefined') return null;
  try {
    let raw = window.localStorage.getItem(snoozeStorageKey(kind, uid, itemId));
    if (!raw && kind === 'task') {
      // Fall back to the legacy key. When it actually holds a value,
      // migrate it onto the current shape right away so the old key does
      // not sit in localStorage forever - and so THAT item's future reads
      // stop paying for this fallback. But an item that was never snoozed
      // under the legacy key (the overwhelmingly common case - most tasks
      // were never snoozed at all) has nothing to migrate, so this extra
      // read runs on every single call for it, forever, not just once.
      const legacyKey = legacyTaskSnoozeKey(uid, itemId);
      const legacyRaw = window.localStorage.getItem(legacyKey);
      if (legacyRaw) {
        raw = legacyRaw;
        try {
          window.localStorage.setItem(snoozeStorageKey(kind, uid, itemId), legacyRaw);
          window.localStorage.removeItem(legacyKey);
        } catch {
          // Migration write/remove failed (quota, private mode) - this read
          // still returns the legacy value; the next read tries again.
        }
      }
    }
    if (!raw) return null;
    const ms = new Date(raw).getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

/*
 * WHO HEARS A SNOOZE CHANGE. A snooze is local storage, not a Firestore
 * field, so nothing re-renders on its own when one is set or runs out. The
 * Waiting on you count (messageWaiting.ts) reads the same snoozes Messages
 * hides its rows with, from a different component, so it subscribes here
 * (PR #1452 review, B17): a snooze set on this tab bumps the version, one set
 * on another tab arrives as a storage event, and a once a minute tick brings
 * back a row whose snooze quietly ran out, the same minute Messages' own
 * snoozeTick re-checks on.
 */
const SNOOZE_RECHECK_MS = 60_000;
const snoozeListeners = new Set<() => void>();
let snoozeVersion = 0;

function notifySnoozeChange(): void {
  snoozeVersion += 1;
  for (const listener of [...snoozeListeners]) listener();
}

/** useSyncExternalStore subscribe: a snooze set here or on another tab, or a minute passing. */
export function subscribeSnoozes(listener: () => void): () => void {
  snoozeListeners.add(listener);
  if (typeof window === 'undefined') return () => { snoozeListeners.delete(listener); };
  const onStorage = (event: { key?: string | null }) => {
    // Bump the version too, or the snapshot would not change until the
    // minute turns and the other tab's snooze would wait up to 60 seconds.
    if (!event.key || event.key.startsWith(SNOOZE_KEY_PREFIX)) {
      snoozeVersion += 1;
      listener();
    }
  };
  window.addEventListener?.('storage', onStorage as EventListener);
  const tick = window.setInterval(listener, SNOOZE_RECHECK_MS);
  return () => {
    snoozeListeners.delete(listener);
    window.removeEventListener?.('storage', onStorage as EventListener);
    window.clearInterval(tick);
  };
}

/**
 * useSyncExternalStore snapshot: changes when a snooze is set on this tab or
 * another, and when the minute turns over, so a memo keyed on it re-reads every snooze.
 */
export function snoozeSnapshot(): string {
  return `${snoozeVersion}:${Math.floor(Date.now() / SNOOZE_RECHECK_MS)}`;
}

function setSnooze(kind: SnoozeKind, uid: string, itemId: string, whenIso: string): void {
  try {
    window.localStorage.setItem(snoozeStorageKey(kind, uid, itemId), whenIso);
  } catch {
    // localStorage can be unavailable (private browsing, full quota). The
    // reminder just will not persist; not worth surfacing an error for it.
  }
  notifySnoozeChange();
}

export function isTaskSnoozed(uid: string | undefined, taskId: string): boolean {
  const until = getSnoozeUntilMs('task', uid, taskId);
  return until !== null && until > Date.now();
}

export function setTaskSnooze(uid: string, taskId: string, whenIso: string): void {
  setSnooze('task', uid, taskId, whenIso);
}

/**
 * REMINDER RESURFACE, simplified: a snooze record whose time has PASSED
 * means the item is no longer hidden - it reappears in its normal spot in
 * the stack. The original FamilyHome pinned a resurfaced item first inside a
 * distinct amber "Reminder" box; this port keeps the underlying hide/
 * resurface mechanic but not that extra pinned-first treatment, a
 * deliberate scope cut called out in this PR's own description.
 */
export function isTaskReminderDue(uid: string | undefined, taskId: string): boolean {
  const until = getSnoozeUntilMs('task', uid, taskId);
  return until !== null && until <= Date.now();
}

export function isPostSnoozed(uid: string | undefined, postId: string): boolean {
  const until = getSnoozeUntilMs('post', uid, postId);
  return until !== null && until > Date.now();
}

export function setPostSnooze(uid: string, postId: string, whenIso: string): void {
  setSnooze('post', uid, postId, whenIso);
}

export function isPostReminderDue(uid: string | undefined, postId: string): boolean {
  const until = getSnoozeUntilMs('post', uid, postId);
  return until !== null && until <= Date.now();
}

/**
 * The generic notification-card snooze (Slice B). One key per notification
 * id, so any card the merged stack renders can be hidden without a
 * per-kind key shape of its own.
 */
export function isNotifSnoozed(uid: string | undefined, notificationId: string): boolean {
  const until = getSnoozeUntilMs('notif', uid, notificationId);
  return until !== null && until > Date.now();
}

export function setNotifSnooze(uid: string, notificationId: string, whenIso: string): void {
  setSnooze('notif', uid, notificationId, whenIso);
}

export function isNotifReminderDue(uid: string | undefined, notificationId: string): boolean {
  const until = getSnoozeUntilMs('notif', uid, notificationId);
  return until !== null && until <= Date.now();
}

/** Three quick, plain-language snooze choices, computed off "right now". */
export function snoozePresets(): { label: string; iso: string }[] {
  const now = new Date();

  const tonight = new Date(now);
  tonight.setHours(19, 0, 0, 0);
  if (tonight.getTime() <= now.getTime()) tonight.setDate(tonight.getDate() + 1);

  const tomorrowMorning = new Date(now);
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
  tomorrowMorning.setHours(8, 0, 0, 0);

  const inThreeDays = new Date(now);
  inThreeDays.setDate(inThreeDays.getDate() + 3);
  inThreeDays.setHours(9, 0, 0, 0);

  return [
    { label: 'Tonight', iso: tonight.toISOString() },
    { label: 'Tomorrow morning', iso: tomorrowMorning.toISOString() },
    { label: 'In 3 days', iso: inThreeDays.toISOString() },
  ];
}

/**
 * Two digits, always. `Date#getMonth()`/`getDate()`/`getHours()`/`getMinutes()`
 * are 0-11/1-31/0-23/0-59 with no leading zero, and `<input type="datetime-local">`
 * requires one.
 */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * An honest default for the real date-and-time picker (Bobbie, 2026-08-18:
 * "Remind me later you should be able to pick the date and time"): tomorrow,
 * 9am, in the picker's own LOCAL-time value shape
 * (`YYYY-MM-DDTHH:mm`, no timezone/seconds - the datetime-local spec). Local
 * time on purpose: the field the browser renders IS local time, so a default
 * that were computed in UTC would silently show the wrong hour in every
 * timezone except UTC+0. Pure and clock-injectable so it is pin-testable
 * without faking the system clock.
 */
export function defaultSnoozeDatetimeLocalValue(now: Date = new Date()): string {
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const y = tomorrow.getFullYear();
  const m = pad2(tomorrow.getMonth() + 1);
  const d = pad2(tomorrow.getDate());
  return `${y}-${m}-${d}T09:00`;
}
